import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";

import {
  getMyEmployerVehicleSettlementSurveyResponses,
  VehicleSettlementSurveyResponsePaginationFilters,
} from "../../api/dossier/getMyEmployerVehicleSettlementSurveyResponses";
import APFilterableList from "../../components/APFilterableList";
import APContactHelpButton from "../../components/buttons/APContactHelpButton";
import BackButton from "../../components/buttons/BackButton";
import HomeButton from "../../components/buttons/HomeButton";
import { AutoCompleteOption } from "../../components/form/APAutoComplete";
import { filterAccountsByPermission } from "../../helpers/user";
import { useAuth } from "../../providers/AuthProvider";
import { VehicleSettlementSurveyResponse } from "../../types/Survey";
import { User } from "../../types/User";
import VehicleSettlementSurveyResponseCard from "../home/components/VehicleSettlementSurveyResponseCard";

export type EmployerViewSurveyFeedbackFilterInputs = {
  accounts: AutoCompleteOption[] | null;
};

export const DEFAULT_PAGE_SIZE = 15;

const EmployerViewSurveyFeedback = ({ me }: { me: User }) => {
  const navigate = useNavigate();
  const [navigationCheckComplete, setNavigationCheck] = useState(false);
  const { getScopedPageUrl } = useAuth();
  const [searchParams] = useSearchParams();
  const firstResponseId = searchParams.get("firstResponseId");
  const [filters, setFilters] =
    useState<VehicleSettlementSurveyResponsePaginationFilters>({
      pageSize: DEFAULT_PAGE_SIZE,
      firstResponseId: firstResponseId ?? undefined,
    });
  const {
    handleSubmit,
    formState: { errors },
  } = useForm<EmployerViewSurveyFeedbackFilterInputs>({
    mode: "onTouched",
    defaultValues: {
      accounts: [],
    },
  });

  const authorisedAccounts = me.accountPermissions
    ? filterAccountsByPermission(
        me.accountPermissions,
        "viewVehicleSettlementSurveys"
      )
    : [];

  useEffect(() => {
    if (authorisedAccounts.length > 0) {
      setNavigationCheck(true);
    } else {
      navigate(getScopedPageUrl("home"), { replace: true });
    }
  });

  const inputsToFilters = () => {
    return {
      ...filters,
      pageSize: DEFAULT_PAGE_SIZE,
      nextPageLink: undefined,
    };
  };

  const renderItems = (responses: VehicleSettlementSurveyResponse[]) => {
    return (
      <Stack spacing={1}>
        {responses.map((response) => (
          <Box key={response.id}>
            <VehicleSettlementSurveyResponseCard
              response={response}
              displayAccount={authorisedAccounts.length > 1}
            />
          </Box>
        ))}
      </Stack>
    );
  };

  return (
    <APFilterableList
      title="Qualitative Feedback"
      filters={filters}
      setFilters={setFilters}
      pageSize={DEFAULT_PAGE_SIZE}
      getItems={getMyEmployerVehicleSettlementSurveyResponses}
      inputsToFilters={inputsToFilters}
      handleSubmit={handleSubmit}
      errors={errors}
      formFields={<></>}
      renderItems={renderItems}
      hideFilters={true}
      isLoading={!navigationCheckComplete}
      isSuccess={navigationCheckComplete}
      abovePaginationContent={
        <Typography variant="labelLarge">
          What do your employees like most about Auto-UX?
        </Typography>
      }
      bottomContent={
        <Stack
          spacing={1}
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            pt: 2,
          }}
        >
          <Box sx={{ display: "flex", columnGap: 1, alignItems: "center" }}>
            <BackButton />
            <HomeButton />
          </Box>
          <APContactHelpButton />
        </Stack>
      }
    />
  );
};

export default EmployerViewSurveyFeedback;
